import FloatingInput from "@/components/ui/floatingInput";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
} from "@/components/ui/form/form";
import useValidateSignUpAccessCode from "@/features/register/hooks/useValidateSignUpAccessCode";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useToast } from "@/components/ui/use-toast";
import { static_errors } from "@/axios/constants";
import SubmitButton from "@/components/ui/submit-button";

const accessCodeSchema = z.object({
	access_code: z.string().min(1, { message: "Access code is required" }).trim(),
});

type AccessCodeFormValues = z.infer<typeof accessCodeSchema>;

interface ValidateAccessCodeStepProps {
	initialCode?: string;
	onValidated: (accessCode: string) => void;
}

const ValidateAccessCodeStep = ({
	initialCode,
	onValidated,
}: ValidateAccessCodeStepProps) => {
	const { toast } = useToast();

	const validateMutation = useValidateSignUpAccessCode({
		onSuccess: (data, variables) => {
			if (data.valid) {
				onValidated(variables.accessCode);
			} else {
				toast({
					variant: "destructive",
					title: "Invalid Access Code",
					description:
						"We couldn't verify that access code. Please double-check it and try again.",
				});
			}
		},
		onError: () => {
			toast({
				variant: "destructive",
				title: "Something went wrong when validating the access code",
				description: static_errors.unexpected_contact_support,
			});
		},
	});

	const form = useForm<AccessCodeFormValues>({
		resolver: zodResolver(accessCodeSchema),
		defaultValues: {
			access_code: initialCode ?? "",
		},
	});

	const handleSubmit = (values: AccessCodeFormValues) => {
		validateMutation.mutate({
			accessCode: values.access_code,
		});
	};

	return (
		<div className="fixed inset-0 flex flex-col items-center justify-between px-6 py-8 bg-white touch-none">
			<div className="flex flex-col items-center justify-center w-full pt-2">
				<h1 className="mb-2 text-4xl font-bold text-textDark">
					Enter Access Code
				</h1>
				<p className="mb-6 text-sm text-center text-textDark max-w-sm">
					Enter your promo code to continue with sign up.
				</p>
			</div>

			<div className="w-full max-w-sm space-y-6">
				<Form {...form}>
					<form
						className="w-full max-w-sm space-y-6"
						onSubmit={form.handleSubmit(handleSubmit)}
					>
						<FormField
							control={form.control}
							name="access_code"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<FloatingInput
											label="Access Code"
											type="text"
											placeholder="Enter your promo code"
											value={field.value}
											onChange={field.onChange}
											error={form.formState.errors.access_code}
											autoFocus
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						<SubmitButton
							type="submit"
							className="w-full"
							disabled={validateMutation.isPending}
							data-testid="validate-access-code-button"
							mutationStatus={validateMutation.status}
							loadingText="Validating Access Code"
						>
							Continue
						</SubmitButton>
					</form>
				</Form>
			</div>
		</div>
	);
};

export default ValidateAccessCodeStep;
